// @flow
import React from 'react';
import PropTypes from 'prop-types';
import { intlShape } from 'react-intl';
import {
  hasLoadedFullUploadElements,
  getFullUploadElements,
  getFullUploadCreationLink,
  areFullUploadElementsAvailable,
} from '../../../services/product-type/ProductTypeService';
import { getProductIdByRoute } from '../../../services/products-route/ProductsRouteService';
import SelectionComponent from '../../common/selection/SelectionComponent';
import SelectionItemComponent from '../../common/selection/SelectionItemComponent';
import * as abstractWindow from '../../../../abstraction/window';

type Props = {
  config: Object,
  params: { size: string },
  guidelinesCallback: () => void,
  elementSelectionCallback: (string) => void,
};

class ProductElementSelectionComponent extends React.Component {
  constructor(props: Props): void {
    super(props);
    this.state = {
      activeId: null,
    };

    this.productSizeId = getProductIdByRoute(this.props.params.size);
  }

  redirectIfNoElements() {
    if (!areFullUploadElementsAvailable(this.productSizeId)) {
      this.onElementSelected('None')();
    }
  }

  UNSAFE_componentWillReceiveProps(): void {
    if (hasLoadedFullUploadElements()) {
      this.redirectIfNoElements();
    }
  }

  onElementSelected(elementName: string): Function {
    return (): void => {
      const elements = this.getElements(this.productSizeId);
      elements.forEach((element) => {
        if (elementName === element.name) {
          this.props.elementSelectionCallback(element.name);
          abstractWindow.location.assign(element.pixelCreationUrl);
        }
      });
    };
  }

  getElements(sizeId: string): Array<Object> {
    const elements = getFullUploadElements(sizeId).concat();
    // add an element for the no tailored finish option

    return [
      {
        name: 'None',
        imageUrl: `/static-assets/mbs/frontend/resources/full-upload-finishes/v3/x1/${sizeId}/none.png`,
        pixelCreationUrl: getFullUploadCreationLink(sizeId),
      },
    ].concat(elements);
  }

  render(): Element {
    const elementHeaderDetails = {
      title: {
        id: 'app.fullUpload.element.title',
      },
      subtitle: {
        id: 'app.fullUpload.element.subtitle',
      },
    };

    const elementGuidelinesDetails = {
      tipText: {
        id: 'app.fullUpload.element.guidelinesTip.text',
      },
      tipLink: {
        id: 'app.fullUpload.element.guidelinesTip.link',
      },
      tipPath: '201462176-Spot-Gloss-and-Gold-Foil-Design-Tips',
    };

    const elementDescriptions = {
      None: {
        subDescription: {
          id: 'app.fullUpload.element.none.description',
          defaultMessage: 'Lets you choose from a range of different papers.',
        },
      },
      'Gold Foil': {
        subDescription: {
          id: 'app.fullUpload.element.foilGold.description',
          defaultMessage:
            'You’ll need your original design file(s) plus a B&W PDF for your gold file. Only available on Super paper.',
        },
      },
      'Silver Foil': {
        subDescription: {
          id: 'app.fullUpload.element.foilSilver.description',
        },
      },
      'Spot Gloss': {
        subDescription: {
          id: 'app.fullUpload.element.spotUV2D.description',
        },
      },
      'Raised Spot Gloss': {
        subDescription: {
          id: 'app.fullUpload.element.spotUV3D.description',
        },
      },
      'Gold foil on one side': {
        subDescription: {
          id: 'app.fullUpload.element.postcard.foilGoldOneSide.description',
        },
      },
      'Gold foil on both sides': {
        subDescription: {
          id: 'app.fullUpload.element.postcard.foilGoldTwoSide.description',
        },
      },
      'Silver foil on one side': {
        subDescription: {
          id: 'app.fullUpload.element.postcard.foilSilverOneSide.description',
        },
      },
      'Silver foil on both sides': {
        subDescription: {
          id: 'app.fullUpload.element.postcard.foilSilverBothSide.description',
        },
      },
    };

    const elements = this.getElements(this.productSizeId);

    return (
      <SelectionComponent
        config={this.props.config}
        headerDetails={elementHeaderDetails}
        guidelinesDetails={elementGuidelinesDetails}
        guidelinesCallback={this.props.guidelinesCallback}
      >
        {elements.map((item: any) => {
          const text = elementDescriptions[item.name];
          return (
            <SelectionItemComponent
              id={item.name.replace(/\s+/g, '-').toLowerCase()}
              key={`element--${item.name.replace(/\s+/g, '-').toLowerCase()}`}
              name={item.name}
              image={item.imageUrl}
              description={
                text && text.description
                  ? this.context.intl.formatMessage(text.description, text.description.values)
                  : ''
              }
              subDescription={text && text.subDescription ? this.context.intl.formatMessage(text.subDescription) : ''}
              linkText={item.name !== 'None' ? `Add ${item.name}` : 'No, thanks'}
              onSelect={this.onElementSelected(item.name)}
            />
          );
        })}
      </SelectionComponent>
    );
  }
}

ProductElementSelectionComponent.displayName = 'ProductElementSelection';

ProductElementSelectionComponent.propTypes = {
  config: PropTypes.shape({
    selectedLocale: PropTypes.string.isRequired,
  }).isRequired,
  params: PropTypes.shape({
    type: PropTypes.string,
    size: PropTypes.string,
  }),
  guidelinesCallback: PropTypes.func,
  elementSelectionCallback: PropTypes.func,
};

ProductElementSelectionComponent.contextTypes = {
  intl: intlShape.isRequired,
};

export default ProductElementSelectionComponent;
